import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { Message } from 'primereact/message'

import { PriceBodyTemplate } from '../../../../../../../components/tableTemplates'
import { useToastContext } from '../../../../../../../contexts'
import {
  ErgebnisAIAnalyze,
  Invoice,
  PatchRequest,
  PropertiesAIResult,
  WebEdiDto,
} from '../../../../../../../data/models'
import { getHandleableError } from '../../../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../../../utils/helper/calculateBreakpointWidth'
import { requestPatchInvoiceById, useInvoiceById } from '../../../../../data'

interface Result extends Omit<PropertiesAIResult, 'status'> {
  field: 'InvoiceAmount' | 'InvoiceNumber' | 'OrderId'
  currentValue?: string | number
  status: {
    status: PropertiesAIResult['status']
    ErrorDetail: PropertiesAIResult['ErrorDetail']
  }
}

type Props = {
  disabled?: boolean
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>
  visible?: boolean
}

export const SectionResult: React.FC<Props> = ({
  disabled = false,
  setVisible,
  visible,
}) => {
  const initialResult: ErgebnisAIAnalyze = {
    ErrorDetail: '',
    Status: 'NOT_SET',
    InvoiceAmount: {
      ErrorDetail: '',
      status: 'NOT_SET',
      value: '',
    },
    InvoiceNumber: {
      ErrorDetail: '',
      status: 'NOT_SET',
      value: '',
    },
    OrderId: {
      ErrorDetail: '',
      status: 'NOT_SET',
      value: '',
    },
  }
  const { id } = useParams()
  const { t } = useTranslation()
  const { data: invoice } = useInvoiceById(id!)
  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)
  const [isChecked, setIsChecked] = useState(false)

  const aiResult = invoice?.Payload.AiAnalysisResult ?? initialResult
  const orderId = aiResult?.OrderId
  const invoiceNumber = aiResult?.InvoiceNumber
  const invoiceAmount = aiResult?.InvoiceAmount

  const results: Result[] = [
    {
      field: t(
        'invoice.attachments.table.result.dialog.table.columns.field.values.ordernumber',
      ),
      status: {
        status: orderId?.status,
        ErrorDetail: orderId?.ErrorDetail,
      },
      value: orderId?.value,
      currentValue: invoice?.Payload.OrderId,
    },
    {
      field: t(
        'invoice.attachments.table.result.dialog.table.columns.field.values.invoicenumber',
      ),
      status: {
        status: invoiceNumber?.status,
        ErrorDetail: invoiceNumber?.ErrorDetail,
      },
      value: invoiceNumber?.value,
      currentValue: invoice?.Payload.InvoiceNumber,
    },
    {
      field: t(
        'invoice.attachments.table.result.dialog.table.columns.field.values.invoiceammount',
      ),
      status: {
        status: invoiceAmount?.status,
        ErrorDetail: invoiceAmount?.ErrorDetail,
      },
      value: invoiceAmount?.value,
      currentValue: invoice?.Payload.ConfirmationInvoiceAmount,
    },
  ]

  const statusTemplate = (rowData: Result) => {
    return (
      <>
        {rowData.status.status === 'APPROVED' && (
          <Button
            icon="pi pi-check"
            outlined
            className="border-circle"
            severity="success"
            aria-label={rowData.status.status}
          />
        )}
        {rowData.status.status === 'ERROR' && (
          <Button
            icon="pi pi-exclamation-triangle"
            outlined
            className="border-circle"
            severity="warning"
            aria-label={rowData.status.status}
            tooltip={t(
              `invoice.attachments.table.result.dialog.messages.ERRORS.${rowData.status.ErrorDetail}.detail`,
            )}
          />
        )}
        {rowData.status.status === 'REJECTED' && (
          <Button
            icon="pi pi-times"
            outlined
            className="border-circle"
            severity="danger"
            aria-label={rowData.status.status}
            tooltip={t(
              `invoice.attachments.table.result.dialog.messages.ERRORS.${rowData.status.ErrorDetail}.detail`,
            )}
          />
        )}
      </>
    )
  }

  const currentValueTemplate = (rowData: Result) => {
    if (
      rowData.field ===
        t(
          'invoice.attachments.table.result.dialog.table.columns.field.values.invoiceammount',
        ) &&
      typeof rowData.currentValue === 'number'
    ) {
      const roundedNumber = Number(rowData.currentValue.toFixed(2))
      return PriceBodyTemplate(roundedNumber)
    }
    return <>{rowData.currentValue}</>
  }

  const valueTemplate = (rowData: Result) => {
    if (
      rowData.field ===
      t(
        'invoice.attachments.table.result.dialog.table.columns.field.values.invoiceammount',
      )
    ) {
      const cleanedString = rowData.value!.replace(/,/g, '')
      const parsedNumber = Number(parseFloat(cleanedString).toFixed(2))
      return PriceBodyTemplate(parsedNumber)
    }
    return <>{rowData.value}</>
  }

  const onTakeOverInvoiceNumber = async () => {
    setIsRequestRunning(true)
    try {
      const patchData: PatchRequest<WebEdiDto<Invoice>> = []
      patchData.push({
        op: invoice?.Payload.InvoiceNumber ? 'replace' : 'add',
        path: '/Payload/InvoiceNumber',
        value: invoice?.Payload.AiAnalysisResult?.InvoiceNumber?.value,
      })
      await requestPatchInvoiceById(id!, patchData)
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      // show error toast
      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        summary: t(
          'invoice.attachments.table.upload.dialog.form.upload.messages.failure.summary',
        ),
        detail: errorMessage,
      })
    } finally {
      setIsRequestRunning(false)
      setVisible!(false)
    }
  }

  const actionTemplate = (rowData: Result) => {
    if (
      rowData.field ===
        t(
          'invoice.attachments.table.result.dialog.table.columns.field.values.invoicenumber',
        ) &&
      (rowData.status.ErrorDetail === 'notSameInvoiceNumbers' ||
        rowData.status.ErrorDetail === 'noInvoiceNumberInBo') &&
      aiResult?.OrderId?.status !== 'REJECTED'
    ) {
      return (
        <div className="flex align-items-center">
          <Checkbox
            inputId="applyPdfValue"
            name="applyPdfValue"
            value="Apply PDF value"
            onChange={e => setIsChecked(e.checked!)}
            checked={isChecked}
          />
          <label htmlFor="applyPdfValue" className="ml-2">
            {t(
              'invoice.attachments.table.result.dialog.table.columns.actions.takePdfValue.label',
            )}
          </label>
        </div>
      )
    }
  }

  const header = <>{t('invoice.attachments.table.result.dialog.title')}</>

  const footer = (
    <>
      <Button
        label={t('common.buttons.back')}
        className="w-full md:w-auto"
        onClick={() => setVisible!(false)}
        disabled={isRequestRunning}
        text={
          (aiResult?.InvoiceNumber?.ErrorDetail === 'notSameInvoiceNumbers' ||
            aiResult?.InvoiceNumber?.ErrorDetail === 'noInvoiceNumberInBo') &&
          aiResult?.OrderId?.status !== 'REJECTED'
        }
      />
      <Button
        label={t('invoice.attachments.table.result.dialog.save.label')}
        icon="pi pi-check"
        className="w-full md:w-auto"
        disabled={!isChecked || isRequestRunning}
        onClick={onTakeOverInvoiceNumber}
        visible={
          (aiResult?.InvoiceNumber?.ErrorDetail === 'notSameInvoiceNumbers' ||
            aiResult?.InvoiceNumber?.ErrorDetail === 'noInvoiceNumberInBo') &&
          aiResult?.OrderId?.status !== 'REJECTED'
        }
      />
    </>
  )

  return (
    <Dialog
      visible={visible && invoice?.Payload.AiAnalysisResult !== undefined}
      style={{ width: '45rem' }}
      breakpoints={{ [calculateBreakpointWidth(45)]: '90vw' }}
      header={header}
      footer={footer}
      onHide={() => setVisible!(false)}
      blockScroll
    >
      <div className="formgrid grid">
        {aiResult?.Status === 'APPROVED' && (
          <Message
            severity="success"
            text={t(
              'invoice.attachments.table.result.dialog.messages.APPROVED',
            )}
          />
        )}
        {aiResult?.Status === 'REJECTED' && (
          <Message
            severity="warn"
            text={t(
              'invoice.attachments.table.result.dialog.messages.REJECTED',
            )}
          />
        )}
        {aiResult?.Status === 'ERROR' && (
          <Message
            severity="error"
            text={
              t(
                `invoice.attachments.table.result.dialog.messages.ERRORS.${aiResult.ErrorDetail}.summary`,
              ) +
              ': ' +
              t(
                `invoice.attachments.table.result.dialog.messages.ERRORS.${aiResult.ErrorDetail}.detail`,
              )
            }
          />
        )}
        {aiResult?.Status !== 'ERROR' && (
          <DataTable value={results} className="mt-3 w-full">
            <Column field="status" body={statusTemplate}></Column>
            <Column
              field="field"
              header={t(
                'invoice.attachments.table.result.dialog.table.columns.field.header',
              )}
            ></Column>
            <Column
              field="currentValue"
              header={t(
                'invoice.attachments.table.result.dialog.table.columns.currentValue.header',
              )}
              body={currentValueTemplate}
            ></Column>
            <Column
              field="value"
              header={t(
                'invoice.attachments.table.result.dialog.table.columns.pdfValue.header',
              )}
              body={valueTemplate}
            ></Column>
            <Column body={actionTemplate}></Column>
          </DataTable>
        )}
      </div>
    </Dialog>
  )
}
